// Phase 6 — Track E T-E3 + Track F T-F3 (final pass).
//
// Class-component ErrorBoundary that catches any uncaught render-time
// exception in its children and shows a friendly fallback. Per
// blueprint §20.3 the fallback NEVER posts to a remote endpoint — error
// details stay client-side. The "Copy details" button puts the
// message + stack into the clipboard for the user to paste manually.
//
// T-F3 final pass: copy strings come from `@dailydeck/shared` so the
// fallback wording matches the rest of the SPA's voice. Mounted at the
// app root by `web/src/app/providers.tsx`.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { Button } from './Button.js';

interface ErrorBoundaryState {
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional render override. Receives the captured error + a reset thunk. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Console-only per blueprint §20.3 — no remote logging.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  copyDetails = (): void => {
    const { error } = this.state;
    if (!error) return;
    const text = `${error.name}: ${error.message}\n${error.stack ?? ''}`;
    void navigator.clipboard?.writeText(text);
  };

  override render(): React.ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="mx-auto max-w-md p-6 text-center flex flex-col gap-3 items-center"
      >
        <h2 className="text-title-2 text-state-danger">{copy.errors.fallback_title}</h2>
        <p className="text-body text-text-secondary">
          The page hit an unexpected error. You can reload to try again, or copy the details
          to share with support.
        </p>
        <div className="flex gap-2 pt-2">
          <Button onClick={() => window.location.reload()}>Reload</Button>
          <Button variant="secondary" onClick={this.copyDetails}>
            Copy details
          </Button>
        </div>
      </div>
    );
  }
}
